import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { Cloud, Database, Download, Loader2, Play, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";

import { ApiError, type ExportProfile, exportsApi, googleDriveApi } from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { toast } from "../lib/toast";

const ENTITIES = ["accounts", "contacts", "inventory", "orders", "dispatches"] as const;

export const Route = createFileRoute("/_app/settings/data-recovery")({
  beforeLoad: () => {
    if (useAuthStore.getState().user?.role !== "admin") throw redirect({ to: "/settings" });
  },
  component: DataRecoveryPage,
});

function DataRecoveryPage() {
  const queryClient = useQueryClient();
  const profileQuery = useQuery({ queryKey: ["exports:profile"], queryFn: exportsApi.profile });
  const runsQuery = useQuery({ queryKey: ["exports:runs"], queryFn: exportsApi.runs });
  const driveQuery = useQuery({
    queryKey: ["google-drive-status"],
    queryFn: googleDriveApi.status,
  });
  const [draft, setDraft] = useState<ExportProfile | null>(null);
  useEffect(() => {
    if (profileQuery.data) setDraft(profileQuery.data);
  }, [profileQuery.data]);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["exports:runs"] });
  const save = useMutation({
    mutationFn: () => exportsApi.updateProfile(draft ?? {}),
    onSuccess: (profile) => {
      setDraft(profile);
      toast.success("Export schedule saved.");
      queryClient.setQueryData(["exports:profile"], profile);
    },
    onError: (error) => toast.error(message(error, "Could not save export settings.")),
  });
  const run = useMutation({
    mutationFn: exportsApi.runNow,
    onSuccess: () => {
      toast.success("Export completed.");
      refresh();
    },
    onError: (error) => toast.error(message(error, "Export failed.")),
  });
  const retry = useMutation({
    mutationFn: exportsApi.retry,
    onSuccess: () => {
      toast.success("Failed artifacts regenerated.");
      refresh();
    },
    onError: (error) => toast.error(message(error, "Retry failed.")),
  });

  if (profileQuery.isLoading || !draft)
    return (
      <p className="flex items-center gap-2 text-sm opacity-60">
        <Loader2 className="size-4 animate-spin" />
        Loading export settings…
      </p>
    );
  if (profileQuery.error)
    return (
      <p role="alert" className="text-sm text-red-600">
        {profileQuery.error.message}
      </p>
    );

  const time = `${String(draft.schedule_hour).padStart(2, "0")}:${String(draft.schedule_minute).padStart(2, "0")}`;
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold">
            <Database className="size-5 opacity-70" />
            Data & recovery
          </h2>
          <p className="mt-1 text-sm opacity-60">
            Create reconstructable Excel snapshots and review every run.
          </p>
        </div>
        <button
          type="button"
          onClick={() => run.mutate()}
          disabled={run.isPending}
          className="inline-flex min-h-11 items-center gap-2 rounded-md bg-[var(--accent)] px-4 text-sm font-medium text-white disabled:opacity-50"
        >
          <Play className="size-4" />
          Run now
        </button>
      </header>

      <section className="space-y-4 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4">
        <div className="flex flex-wrap gap-6">
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
            />
            Daily scheduled exports
          </label>
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.drive_upload_enabled}
              disabled={!driveQuery.data?.connected}
              onChange={(e) => setDraft({ ...draft, drive_upload_enabled: e.target.checked })}
            />
            <Cloud className="size-4 opacity-60" />
            Upload to Google Drive
          </label>
          <label className="text-sm">
            <span className="mb-1 block">Time (organization timezone)</span>
            <input
              className="form-input"
              type="time"
              value={time}
              onChange={(e) => {
                const hour = Number(e.target.value.slice(0, 2));
                const minute = Number(e.target.value.slice(3, 5));
                setDraft({ ...draft, schedule_hour: hour, schedule_minute: minute });
              }}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block">Keep local files</span>
            <select
              className="form-input"
              value={draft.local_retention_days}
              onChange={(e) => setDraft({ ...draft, local_retention_days: Number(e.target.value) })}
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
          </label>
        </div>
        <fieldset>
          <legend className="mb-2 text-sm font-medium">Included records</legend>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {ENTITIES.map((entity) => (
              <label
                key={entity}
                className="flex min-h-11 items-center gap-2 rounded-md border border-[var(--border)] px-3 text-sm capitalize"
              >
                <input
                  type="checkbox"
                  checked={draft.entities.includes(entity)}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      entities: e.target.checked
                        ? [...draft.entities, entity]
                        : draft.entities.filter((item) => item !== entity),
                    })
                  }
                />
                {entity}
              </label>
            ))}
          </div>
        </fieldset>
        <p className="text-xs opacity-60">
          Exports include stable record and relationship IDs, historical inactive/deleted records,
          and one row per Order or Delivery line. Local copies remain available during the selected
          retention period.
        </p>
        {!driveQuery.data?.connected && (
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Connect Google Drive in{" "}
            <Link to="/settings/google-drive" className="underline">
              Integrations
            </Link>{" "}
            before enabling cloud uploads.
          </p>
        )}
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending || draft.entities.length === 0}
          className="min-h-11 rounded-md border border-[var(--border)] px-4 text-sm font-medium disabled:opacity-50"
        >
          Save schedule
        </button>
      </section>

      <section className="space-y-3">
        <h3 className="font-semibold">Run history</h3>
        {runsQuery.isLoading ? (
          <p className="text-sm opacity-60">Loading history…</p>
        ) : runsQuery.data?.items.length ? (
          runsQuery.data.items.map((item) => (
            <article
              key={item.id}
              className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="font-medium capitalize">{item.trigger} export</span>
                  <span
                    className={`ml-2 text-xs font-medium uppercase ${item.status === "succeeded" ? "text-emerald-600" : "text-red-600"}`}
                  >
                    {item.status}
                  </span>
                  <div className="text-xs opacity-60">
                    {new Date(item.started_at).toLocaleString()}
                  </div>
                </div>
                {item.artifacts.some((artifact) =>
                  ["failed", "upload_failed"].includes(artifact.status),
                ) && (
                  <button
                    type="button"
                    onClick={() => retry.mutate(item.id)}
                    className="inline-flex min-h-11 items-center gap-2 rounded-md border border-[var(--border)] px-3 text-sm"
                  >
                    <RotateCcw className="size-4" />
                    Retry failed
                  </button>
                )}
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {item.artifacts.map((artifact) => (
                  <div
                    key={artifact.id}
                    className="flex items-center justify-between gap-3 rounded-md bg-[var(--hover)] px-3 py-2 text-sm"
                  >
                    <div>
                      <div className="capitalize">
                        {artifact.entity} · {artifact.row_count} rows
                      </div>
                      <div className="text-xs opacity-60">{artifact.status}</div>
                    </div>
                    {["local", "uploaded", "upload_failed"].includes(artifact.status) && (
                      <a
                        href={exportsApi.downloadUrl(artifact.id)}
                        className="flex min-h-11 items-center gap-1 text-[var(--accent)]"
                      >
                        <Download className="size-4" />
                        Download
                      </a>
                    )}
                  </div>
                ))}
              </div>
              {item.error && <p className="mt-2 text-xs text-red-600">{item.error}</p>}
            </article>
          ))
        ) : (
          <p className="rounded-xl border border-dashed border-[var(--border)] p-8 text-center text-sm opacity-60">
            No exports have run yet.
          </p>
        )}
      </section>
    </div>
  );
}

function message(error: unknown, fallback: string): string {
  if (error instanceof ApiError)
    return error.reauthRequired
      ? "Sign out and sign in again before changing recovery settings."
      : error.message;
  return fallback;
}
